import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, Box, Typography, Link, CircularProgress, Stack } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const getExt = (fileName = '') => fileName.split('.').pop().toLowerCase();

export default function DocumentPreview({ document: doc }) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  const ext = getExt(doc.name);
  const url = doc.url;

  useEffect(() => {
    setContent('');
    setLoading(true);
    if (['txt', 'md', 'json'].includes(ext)) {
      axios.get(url)
        .then((res) => setContent(typeof res.data === 'string' ? res.data : JSON.stringify(res.data, null, 2)))
        .catch((err) => console.error('Error fetching file content:', err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [doc, ext, url]);

  const renderBody = () => {
    if (loading) {
      return (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Stack>
      );
    }
    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
      return (
        <Box
          component="img"
          src={url}
          alt={doc.name}
          sx={{ maxWidth: '100%', height: 'auto', borderRadius: 1 }}
        />
      );
    }
    if (ext === 'pdf') {
      return (
        <Box sx={{ width: '100%', height: { xs: '70vh', md: '80vh' } }}>
          <iframe
            src={url}
            title={doc.name}
            style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
          />
        </Box>
      );
    }
    if (['txt', 'md', 'json'].includes(ext)) {
      return (
        <Box
          component="pre"
          sx={{
            p: 2,
            bgcolor: 'background.default',
            borderRadius: 1,
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            fontSize: '0.875rem',
            maxHeight: '70vh',
            overflow: 'auto',
            m: 0,
          }}
        >
          {content}
        </Box>
      );
    }
    if (['mp4', 'webm', 'ogg'].includes(ext)) {
      return (
        <Box
          component="video"
          controls
          sx={{ width: '100%', maxHeight: '70vh' }}
        >
          <source src={url} type={`video/${ext}`} />
        </Box>
      );
    }
    if (['mp3', 'wav'].includes(ext)) {
      return (
        <Box component="audio" controls sx={{ width: '100%' }}>
          <source src={url} type={`audio/${ext}`} />
        </Box>
      );
    }
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
      const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      return (
        <Box sx={{ width: '100%', height: { xs: '70vh', md: '80vh' } }}>
          <iframe
            src={viewerUrl}
            title={doc.name}
            style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
          />
        </Box>
      );
    }
    return (
      <Typography color="text.secondary">
        Bu dosya türü için önizleme yok.{' '}
        <Link href={url} target="_blank" rel="noopener noreferrer">İndir</Link>
      </Typography>
    );
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} spacing={1}>
          <Typography variant="h6" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {doc.name}
          </Typography>
          <Link
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
          >
            Yeni sekmede aç <OpenInNewIcon fontSize="small" />
          </Link>
        </Stack>
        {renderBody()}
      </CardContent>
    </Card>
  );
}
